import { useCallback, useEffect, useState } from "react";
import {
  assignManager,
  changeManager,
  getManagerChain,
  removeManager,
} from "../hierarchyApi";
import ManagerEmployeeSelect from "./ManagerEmployeeSelect";

export default function ReportingInfoSection({ employeeId, employee, onUpdated }) {
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedManager, setSelectedManager] = useState(null);
  const [editing, setEditing] = useState(false);

  const mgr = employee?.reporting_manager_employee;
  const level = employee?.hierarchy_level ?? 0;
  const directCount = employee?.direct_reports_count ?? 0;

  const loadChain = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const data = await getManagerChain(employeeId);
      setChain(Array.isArray(data) ? data : []);
    } catch {
      setChain([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadChain();
  }, [loadChain, mgr?.employee_id]);

  const handleSaveManager = async () => {
    if (!selectedManager?.value) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = { manager_id: selectedManager.value };
      if (mgr?.employee_id) {
        await changeManager(employeeId, payload);
      } else {
        await assignManager(employeeId, payload);
      }
      setSuccess("Reporting manager updated.");
      setSelectedManager(null);
      setEditing(false);
      onUpdated?.();
      await loadChain();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update manager");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveManager = async () => {
    if (!mgr?.employee_id) return;
    if (!window.confirm("Remove the current reporting manager?")) return;
    setSaving(true);
    setError("");
    try {
      await removeManager(employeeId);
      setSuccess("Manager removed.");
      setEditing(false);
      onUpdated?.();
      await loadChain();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove manager");
    } finally {
      setSaving(false);
    }
  };

  const mgrLabel = mgr
    ? `${mgr.employee_code || ""} – ${mgr.employee_name}`.replace(/^ – /, "")
    : "—";

  const chainLabel = loading
    ? "Loading…"
    : chain.length === 0
      ? "No reporting chain"
      : chain
        .map((item) => {
          const code = item.employee_code ? `${item.employee_code} – ` : "";
          const desig = item.designation ? ` (${item.designation})` : "";
          return `${item.level}. ${code}${item.employee_name}${desig}`;
        })
        .join(" → ");

  return (
    <div className="ec-report">
      <div className="ec-org-group-hd ec-report-hd">
        <div className="ec-org-group-icon team" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div className="ec-org-group-title">Reporting Information</div>
      </div>

      <div className="ec-report-grid">
        <div className="ec-report-cell">
          <span>Reporting Manager</span>
          <strong>{mgrLabel}</strong>
        </div>
        <div className="ec-report-cell">
          <span>Department</span>
          <strong>{employee?.department?.name || "—"}</strong>
        </div>
        <div className="ec-report-cell">
          <span>Designation</span>
          <strong>{employee?.designation?.title || "—"}</strong>
        </div>
        <div className="ec-report-cell">
          <span>Hierarchy Level</span>
          <strong>{level}</strong>
        </div>
        <div className="ec-report-cell">
          <span>Direct Reports</span>
          <strong>{directCount}</strong>
        </div>
      </div>

      <div className="ec-report-chain">
        <span>Reporting Chain</span>
        <p>{chainLabel}</p>
      </div>

      {editing ? (
        <div className="ec-report-edit">
          <ManagerEmployeeSelect
            value={selectedManager}
            onChange={setSelectedManager}
            excludeEmployeeId={employeeId}
            placeholder="Search manager (Admin / HR / Manager)…"
            compact
          />
          <div className="ec-report-actions">
            <button
              type="button"
              disabled={!selectedManager?.value || saving}
              onClick={handleSaveManager}
              className="ec-report-btn primary"
            >
              {saving ? "Saving…" : mgr ? "Change Manager" : "Assign Manager"}
            </button>
            {mgr ? (
              <button
                type="button"
                disabled={saving}
                onClick={handleRemoveManager}
                className="ec-report-btn"
              >
                Remove Manager
              </button>
            ) : null}
            <button
              type="button"
              disabled={saving}
              onClick={() => { setEditing(false); setError(""); setSelectedManager(null); }}
              className="ec-report-btn"
            >
              Cancel
            </button>
          </div>
          {error ? <p className="ec-report-msg err">{error}</p> : null}
          {success ? <p className="ec-report-msg ok">{success}</p> : null}
        </div>
      ) : (
        <div className="ec-report-link-row">
          <button type="button" className="ec-report-link" onClick={() => setEditing(true)}>
            Change Reporting Manager
          </button>
          {success ? <p className="ec-report-msg ok">{success}</p> : null}
        </div>
      )}
    </div>
  );
}
